import { UserPreferenceKey, withUserPreferenceSync } from '@mitsubishi/core';
import {
  MODEL_SEARCH_BRAND_ID_FIELD,
  MODEL_SEARCH_SEGMENT_ID_FIELD,
  ModelSearchFilter,
} from '@mitsubishi/shared/data-access';

import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';

type CatalogScope = {
  brandIds: number[];
  segmentIds: number[];
};

/** The payload of `CATALOG_FILTERS`: one scope per product class. */
type CatalogScopeState = {
  filters: Record<string, CatalogScope>;
};

const initialState: CatalogScopeState = {
  filters: {},
};

/**
 * The brands and segments a user works with, per product class. It is a preference, not a query: the
 * model search derives its brand/segment filters from here (`ModelSearchFiltersStore`), and the unit
 * templates page filters its own list with it without searching any model.
 */
export const CatalogScopeStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withUserPreferenceSync<CatalogScopeState>({ preferenceKey: UserPreferenceKey.CATALOG_FILTERS }),
  withMethods(store => {
    const scopeOf = (classId: number) => store.filters()[classId];

    const patchScope = (classId: number, scope: Partial<CatalogScope>) =>
      patchState(store, state => ({
        filters: { ...state.filters, [classId]: { ...state.filters[classId], ...scope } as CatalogScope },
      }));

    return {
      /** Reads `filters`, so call it inside a `computed` to follow later changes. */
      brandIds: (classId: number): number[] => scopeOf(classId)?.brandIds ?? [],
      segmentIds: (classId: number): number[] => scopeOf(classId)?.segmentIds ?? [],

      setBrandIds: (classId: number, brandIds: number[]) => patchScope(classId, { brandIds }),
      setSegmentIds: (classId: number, segmentIds: number[]) => patchScope(classId, { segmentIds }),

      /**
       * The scope as model search filters. An empty selection is left out rather than sent as an empty
       * list, which the search would read as "match nothing".
       */
      searchFilters: (classId: number | undefined): ModelSearchFilter => {
        const scope = classId === undefined ? undefined : scopeOf(classId);

        return {
          ...(scope?.brandIds?.length ? { [MODEL_SEARCH_BRAND_ID_FIELD]: scope.brandIds } : {}),
          ...(scope?.segmentIds?.length ? { [MODEL_SEARCH_SEGMENT_ID_FIELD]: scope.segmentIds } : {}),
        };
      },
    };
  })
);
